// Interner 1:1-Chat (Pille) — reine Helfer ohne DB-Zugriff.
//
// Es gibt bewusst keine Unterhaltungs-Tabelle: eine Unterhaltung IST das Paar aus zwei
// Personen. Diese Datei leitet daraus die Gesprächsliste, die Ungelesen-Zähler und die
// Datumstrenner ab. Alles vitest-getestet, damit die Pillen-Ansicht dünn bleibt.

export interface ChatNachricht {
  id: string;
  absender_id: string;
  empfaenger_id: string | null;
  gruppe_id: string | null;
  text: string | null;
  bild_path: string | null;
  bild_name: string | null;
  created_at: string;
  gelesen_am: string | null;
  /** #549: Verweis auf eine vorhandene Unterlage (keine Kopie). */
  anhang?: ChatAnhang | null;
}

/**
 * Verweis auf eine Unterlage, die schon in der App liegt — Drive-Datei, Mail-Anhang oder
 * Ticket-Anhang. Es wird nichts kopiert; geöffnet wird mit den Rechten des Empfängers.
 */
export interface ChatAnhang {
  quelle: 'drive' | 'email' | 'ticket';
  /** Drive-Datei-ID bzw. email_anhaenge.id / ticket_anhaenge.id */
  id: string;
  name: string;
  typ?: string | null;
  groesse?: number | null;
  /** Speicherpfad (nur Ticket-Anhänge) */
  pfad?: string | null;
  /** Kurze Herkunft für die Karte, z. B. „0015 Rosenweg 30“ oder „T-0203“ */
  kontext?: string | null;
}

/** Bild (Vorschau in der Blase) oder sonstige Datei (Datei-Karte)? */
export const istBildDatei = (name: string | null | undefined): boolean => /\.(jpe?g|png|gif|webp|heic)$/i.test(name ?? '');

export interface Kollege {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url?: string | null;
}

/** Wer ist bei dieser Nachricht das Gegenüber? Nur für 1:1 gedacht — der DB-CHECK
 *  garantiert dort ein gesetztes empfaenger_id (Gruppen laufen über gruppe_id). */
export function partnerVon(n: ChatNachricht, meineId: string): string {
  return (n.absender_id === meineId ? n.empfaenger_id : n.absender_id) ?? '';
}

/**
 * Nachrichten eines Paars, älteste zuerst (Leserichtung im Verlauf). Gruppenzeilen werden
 * ausdrücklich ausgeschlossen: seit empfaenger_id nullable ist, könnte sonst eine eigene
 * Gruppennachricht unter dem leeren Partner-Schlüssel landen oder eine fremde Gruppennachricht
 * fälschlich als 1:1-Nachricht dieser Person erscheinen.
 */
export function verlaufMit(nachrichten: ChatNachricht[], meineId: string, partnerId: string): ChatNachricht[] {
  return nachrichten
    .filter((n) => !n.gruppe_id && partnerVon(n, meineId) === partnerId)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}

/** Anzeigename mit Rückfall auf die E-Mail — leere full_name-Felder kommen vor. */
export function kollegeName(k: Kollege | undefined | null): string {
  const name = (k?.full_name || '').trim();
  if (name) return name;
  const mail = (k?.email || '').trim();
  return mail || 'Unbekannt';
}

/** Initialen für den Avatar-Kreis: „Björn Schwede" → „BS". */
export function initialen(name: string): string {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  if (!teile.length) return '?';
  if (teile.length === 1) return teile[0].slice(0, 2).toUpperCase();
  return (teile[0][0] + teile[teile.length - 1][0]).toUpperCase();
}

/** Vorschauzeile in der Gesprächsliste. */
export function vorschauText(n: ChatNachricht): string {
  const t = (n.text || '').trim();
  if (t) return t;
  if (n.anhang) return `📎 ${n.anhang.name}`;
  if (n.bild_path) return istBildDatei(n.bild_name || n.bild_path) ? '📷 Bild' : `📄 ${n.bild_name || 'Datei'}`;
  return '';
}

/** „14:32" heute, „Gestern", sonst „05.08." — kurz genug für die schmale Liste. */
export function zeitKurz(iso: string, jetzt: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const tag = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (tag(d) === tag(jetzt)) return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const gestern = new Date(jetzt.getTime() - 24 * 60 * 60 * 1000);
  if (tag(d) === tag(gestern)) return 'Gestern';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

/** Datumstrenner im Verlauf: „Heute", „Gestern" oder „Montag, 05.08.2026". */
export function tagesTrenner(iso: string, jetzt: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const tag = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (tag(d) === tag(jetzt)) return 'Heute';
  const gestern = new Date(jetzt.getTime() - 24 * 60 * 60 * 1000);
  if (tag(d) === tag(gestern)) return 'Gestern';
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Der Chat wird wöchentlich geleert (Cron „chat-aufraeumen-woechentlich", Sonntag 03:30,
 * Aufbewahrung 7 Tage). Diese Zahl steht auch in der Oberfläche, damit niemand den Chat
 * für ein Archiv hält — Wichtiges gehört ins Ticket.
 */
export const AUFBEWAHRUNG_TAGE = 7;

/** Eine Chat-Gruppe: entweder Team-Chat (team_id gesetzt) oder frei gegründet. */
export interface ChatGruppe {
  id: string;
  name: string | null;        // nur freie Gruppen
  team_id: string | null;
  team_name: string | null;   // aus teams.name, damit Umbenennen sofort greift
  erstellt_von: string | null;
  mitglieder: string[];       // profile_ids (Team-Chat: aus team_members)
  gelesen_bis: string | null; // eigener Lesestand
}

export type UnterhaltungArt = 'person' | 'gruppe';

/**
 * Listeneintrag der Pille — Person ODER Gruppe. `letzte` ist bei Gruppen ohne bisherige
 * Nachricht `null` (z. B. ein frisch angelegter Team-Chat) — Personen haben immer eine
 * Nachricht, weil sie nur über eine bestehende Nachricht in die Liste kommen.
 */
export interface Unterhaltung {
  art: UnterhaltungArt;
  id: string;
  letzte: ChatNachricht | null;
  ungelesen: number;
}

/** Team-Chats erben den Teamnamen, damit Umbenennen nicht auseinanderläuft. */
export function gruppenName(g: ChatGruppe): string {
  const eigen = (g.name || '').trim();
  if (eigen) return eigen;
  const team = (g.team_name || '').trim();
  return team || 'Gruppe';
}

/**
 * Ungelesen in einer Gruppe: alles Fremde nach dem eigenen Lesestand.
 * Eigene Nachrichten zählen nie — sonst stünde der Zähler direkt nach dem Senden auf 1.
 */
export function ungelesenInGruppe(
  nachrichten: ChatNachricht[], gruppeId: string, meineId: string, gelesenBis: string | null,
): number {
  const grenze = gelesenBis ? new Date(gelesenBis).getTime() : 0;
  return nachrichten.filter((n) =>
    n.gruppe_id === gruppeId && n.absender_id !== meineId && new Date(n.created_at).getTime() > grenze,
  ).length;
}

/** Nachrichten einer Gruppe, älteste zuerst (Leserichtung im Verlauf). */
export function verlaufInGruppe(nachrichten: ChatNachricht[], gruppeId: string): ChatNachricht[] {
  return nachrichten
    .filter((n) => n.gruppe_id === gruppeId)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}

/**
 * EINE Liste aus Personen und Gruppen. ALLE übergebenen Gruppen landen in der Liste —
 * auch ohne bisherige Nachricht (`letzte: null`), sonst wäre ein frischer Team-Chat oder
 * eine gerade gegründete Gruppe für niemanden erreichbar: man kann nur schreiben, was man
 * vorher aus dieser Liste ausgewählt hat.
 *
 * Sortierung: Unterhaltungen mit Nachricht zuerst, neueste zuerst — wie bisher. Gruppen
 * ohne Nachricht stehen danach, alphabetisch nach Anzeigenamen (Personen ohne Nachricht
 * gibt es nicht, sie kommen nur über eine bestehende Nachricht in die Liste).
 */
export function gruppiereAlles(
  nachrichten: ChatNachricht[], meineId: string, gruppen: ChatGruppe[],
): Unterhaltung[] {
  const raus = new Map<string, Unterhaltung>();
  for (const n of nachrichten) {
    if (n.gruppe_id) {
      const g = gruppen.find((x) => x.id === n.gruppe_id);
      if (!g) continue; // Gruppe verlassen oder gelöscht
      const vorhanden = raus.get(`g:${g.id}`);
      if (!vorhanden) {
        raus.set(`g:${g.id}`, {
          art: 'gruppe', id: g.id, letzte: n,
          ungelesen: ungelesenInGruppe(nachrichten, g.id, meineId, g.gelesen_bis),
        });
        continue;
      }
      if (vorhanden.letzte && new Date(n.created_at).getTime() > new Date(vorhanden.letzte.created_at).getTime()) {
        vorhanden.letzte = n;
      }
      continue;
    }
    const partner = partnerVon(n, meineId);
    const schluessel = `p:${partner}`;
    const vorhanden = raus.get(schluessel);
    const ungelesen = n.empfaenger_id === meineId && !n.gelesen_am ? 1 : 0;
    if (!vorhanden) {
      raus.set(schluessel, { art: 'person', id: partner, letzte: n, ungelesen });
      continue;
    }
    vorhanden.ungelesen += ungelesen;
    if (vorhanden.letzte && new Date(n.created_at).getTime() > new Date(vorhanden.letzte.created_at).getTime()) {
      vorhanden.letzte = n;
    }
  }
  // Gruppen ohne bisherige Nachricht ergänzen — sie sind sonst nicht auswählbar.
  for (const g of gruppen) {
    const schluessel = `g:${g.id}`;
    if (!raus.has(schluessel)) raus.set(schluessel, { art: 'gruppe', id: g.id, letzte: null, ungelesen: 0 });
  }
  const gruppenNamen = new Map(gruppen.map((g) => [g.id, gruppenName(g)]));
  return [...raus.values()].sort((a, b) => {
    if (a.letzte && b.letzte) return new Date(b.letzte.created_at).getTime() - new Date(a.letzte.created_at).getTime();
    if (a.letzte && !b.letzte) return -1;
    if (!a.letzte && b.letzte) return 1;
    // Beide ohne Nachricht: alphabetisch nach Anzeigenamen (nur Gruppen betroffen).
    const na = a.art === 'gruppe' ? (gruppenNamen.get(a.id) ?? '') : a.id;
    const nb = b.art === 'gruppe' ? (gruppenNamen.get(b.id) ?? '') : b.id;
    return na.localeCompare(nb, 'de');
  });
}

/** Punkt an der ruhenden Pille: 1:1 und Gruppen zusammen. */
export function zaehleUngelesenGesamt(
  nachrichten: ChatNachricht[], meineId: string, gruppen: ChatGruppe[],
): number {
  const einzeln = nachrichten.filter((n) => !n.gruppe_id && n.empfaenger_id === meineId && !n.gelesen_am).length;
  const inGruppen = gruppen.reduce(
    (summe, g) => summe + ungelesenInGruppe(nachrichten, g.id, meineId, g.gelesen_bis), 0,
  );
  return einzeln + inGruppen;
}

/**
 * Für die Chat-Liste in der Kopfleiste: nur Gespräche mit Ungelesenem, jüngste zuerst.
 * Gruppen ohne bisherige Nachricht (`letzte === null`) können Ungelesenes nicht datieren
 * und rutschen deshalb ans Ende — verschwinden aber nicht aus der Liste.
 */
export function mitUngelesenem(unterhaltungen: Unterhaltung[]): Unterhaltung[] {
  return unterhaltungen
    .filter((u) => u.ungelesen > 0)
    .sort((a, b) => (b.letzte ? Date.parse(b.letzte.created_at) : 0) - (a.letzte ? Date.parse(a.letzte.created_at) : 0));
}

/**
 * Stabile Kennung eines offenen Gesprächs.
 *
 * Die Pille hält das offene Gespräch als Objekt ({ art, id }) und baut bei jeder Auswahl
 * ein neues — auch dann, wenn dasselbe Gespräch schon offen ist. Ein useEffect, der an
 * diesem Objekt hängt, vergleicht die Referenz und feuert deshalb auch beim Klick auf die
 * bereits offene Unterhaltung. Genau daran ging der getippte Entwurf verloren. Wer auf
 * „Gespräch gewechselt" reagieren will, hängt sich an diesen Schlüssel statt an das Objekt.
 */
export function zielSchluessel(ziel: { art: UnterhaltungArt; id: string } | null): string | null {
  return ziel ? `${ziel.art}:${ziel.id}` : null;
}
